import { useId, useState, type FormEvent } from 'react';
import type { CreateTenantDto, Tenant, WorkspaceNameConflict } from '@mg/shared';
import { api, ApiRequestError, asWorkspaceNameConflict } from '../../lib/api';
import { useTenant } from '../../auth/TenantContext';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../ui/Button';
import { FormField, Input, Textarea } from '../ui/Field';
import { WorkspaceNameConflictNotice } from './WorkspaceNameConflictNotice';

const BRAND_VOICE_HINT =
  "A system prompt that defines your brand's goals and guidelines for the AI — the strongest, most general voice guidance.";
const COMPANY_VOICE_HINT =
  "The organization's positioning and messaging for the AI — refines the Brand Voice above. Shared across the workspace.";

/**
 * Shared "create a new Brand Workspace" form. On success it makes the new
 * workspace active, refreshes the workspace list + auth principal, then calls
 * `onCreated`. Used both inside the create modal and the first-login gate.
 */
export function CreateWorkspaceForm({
  onCreated,
  onCancel,
  submitLabel = 'Create Brand Workspace',
}: {
  onCreated: (tenant: Tenant) => void;
  /** Omitted on the first-login gate (which is non-dismissable). */
  onCancel?: () => void;
  submitLabel?: string;
}) {
  const { refresh, setActiveTenant } = useTenant();
  const { refresh: refreshPrincipal } = useAuth();

  const baseId = useId();
  const nameId = `${baseId}-name`;
  const descId = `${baseId}-desc`;
  const voiceId = `${baseId}-voice`;
  const companyVoiceId = `${baseId}-company-voice`;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [brandVoice, setBrandVoice] = useState('');
  const [companyVoice, setCompanyVoice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<WorkspaceNameConflict | null>(null);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const canSubmit = trimmedName.length > 0 && trimmedDescription.length > 0 && !submitting;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setConflict(null);
    try {
      const dto: CreateTenantDto = {
        name: trimmedName,
        description: trimmedDescription,
      };
      const voice = brandVoice.trim();
      if (voice) dto.brandVoice = voice;
      const company = companyVoice.trim();
      if (company) dto.companyVoice = company;

      const created = await api.createTenant(dto);
      // Refresh the list, make the new workspace active, refresh the principal.
      await refresh();
      setActiveTenant(created.slug);
      await refreshPrincipal();
      onCreated(created);
    } catch (err) {
      const nameConflict = asWorkspaceNameConflict(err);
      if (nameConflict) {
        setConflict(nameConflict);
      } else {
        setError(
          err instanceof ApiRequestError
            ? err.message
            : 'Could not create the workspace. The API may be unreachable — try again.',
        );
      }
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField label="Brand Workspace name" htmlFor={nameId}>
        <Input
          id={nameId}
          value={name}
          onChange={(e) => {
            setConflict(null);
            setName(e.target.value);
          }}
          placeholder="e.g. Northwind Orthopedics"
          autoFocus
          required
          maxLength={120}
        />
      </FormField>

      <FormField label="Brand Description" htmlFor={descId}>
        <Textarea
          id={descId}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What your brand is about — products, audience, and goals."
          rows={3}
          required
        />
      </FormField>

      <FormField label="Brand Voice" htmlFor={voiceId} hint={BRAND_VOICE_HINT}>
        <Textarea
          id={voiceId}
          value={brandVoice}
          onChange={(e) => setBrandVoice(e.target.value)}
          placeholder="Tone, do's and don'ts, audience, and goals for your brand's AI-generated content."
          rows={5}
        />
      </FormField>

      <FormField label="Company Voice" htmlFor={companyVoiceId} hint={COMPANY_VOICE_HINT}>
        <Textarea
          id={companyVoiceId}
          value={companyVoice}
          onChange={(e) => setCompanyVoice(e.target.value)}
          placeholder="The organization's positioning and messaging — shared across the workspace."
          rows={5}
        />
      </FormField>

      {conflict && <WorkspaceNameConflictNotice conflict={conflict} />}

      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-error-dark">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        )}
        <Button type="submit" loading={submitting} disabled={!canSubmit}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
